import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import seeqLogoDarkBlueSm from 'resources/img/Seeq_logo_darkBlue_sm.png';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { isSystemTest } from '@/core/utilities';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { useHasValidFeature, useLicense } from '@/licenseManagement/licenseManagement.store';
import { adminContactEmail, adminContactName, auditingEnabled } from '@/services/systemConfiguration.utilities';
import {
  useShowSystemWarning,
  useSuppressUserLimitWarning,
  useSystemMessage,
  useSystemWarningActions,
  useWarningComponents,
} from '@/systemWarning/systemWarning.store';
import { buildEmailLink } from '@/utilities/utilities';
import { setSystemMessageHash } from '@/workbench/workbench.actions';
import { Alert } from '@seeqdev/qomponents';

export const SystemWarning = () => {
  const warningComponents = useWarningComponents();
  const systemMessage = useSystemMessage();
  const showSystemWarningFromStore = useShowSystemWarning();
  const suppressUserLimitWarning = useSuppressUserLimitWarning();
  const { setSystemMessage, disableSystemWarning, disableUserLimitWarning } = useSystemWarningActions();

  const currentUser = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser);
  const license = useLicense();
  const { t } = useTranslation();

  const isUserAdmin = currentUser?.isAdmin;
  const showSystemWarning =
    !_.isEmpty(warningComponents) &&
    showSystemWarningFromStore &&
    // TODO CRAB-12149 Should non-admins get hardware warnings?
    isUserAdmin;
  const nodeEnv = process.env.NODE_ENV;
  const isProduction = nodeEnv === 'production' && !isSystemTest();
  const showSystemMessage = !!systemMessage && !sqWorkbenchStore.isSystemMessageDismissed(systemMessage);
  const showAuditWarning = !useHasValidFeature(Feature.Audit_Trail) && auditingEnabled();

  const userLimitRestrict = license.userLimitRestrict ?? 0;
  const userCount = license.userCount ?? 0;
  const userLimitWarn = license.userLimitWarn ?? 0;

  const showUserLimitRestriction = userLimitRestrict > 0 && userCount >= userLimitRestrict;
  const showUserLimitWarning =
    !showUserLimitRestriction &&
    userLimitWarn > 0 &&
    userLimitRestrict > 0 &&
    userCount >= userLimitWarn &&
    !suppressUserLimitWarning;

  const contactEmail = buildEmailLink(
    adminContactEmail(),
    adminContactName(),
    'SYSTEM_WARNINGS.INFO_TEXT_EMAIL_SUBJECT',
    'SYSTEM_WARNINGS.INFO_TEXT_EMAIL_BODY',
  );

  // Warning banner that informs about server hardware problems
  if (showSystemWarning && isProduction) {
    return (
      <Alert
        id="systemWarningBanner"
        testId="systemWarningBanner"
        variant="warning"
        dismissible
        onClose={disableSystemWarning}
        extraClassNames="pt20 pb20 flexColumnContainer flexCenter warningBannerHeight">
        <div className="flexRowContainer">
          <p>
            <span>{t('LICENSE.THANKS_FOR_USING')}</span>
          </p>
          <p>
            {/* <!-- Main intro text if the user is not an admin. Tells them to email the admin. --> */}
            {!isUserAdmin && (
              <ContainerWithHTML
                testId="emailAdmin"
                content={t('SYSTEM_WARNINGS.INFO_TEXT_USER', {
                  CONTACT: contactEmail,
                })}
              />
            )}
            {isUserAdmin && (
              <>
                {/* <!-- Main intro text if the user is an admin. Tells them to improve HW. --> */}
                <span>{t('SYSTEM_WARNINGS.INFO_TEXT_ADMIN_1')}</span>&nbsp;
                <a
                  target="_blank"
                  href="https://telemetry.seeq.com/support-link/kb/latest/on-premise/installing-seeq-server#InstallingSeeqServer-HardwareRequirements"
                  data-testid="seeqHardwareLink"
                  rel="noopener">
                  <span>{t('SYSTEM_WARNINGS.INFO_TEXT_ADMIN_2')}</span>
                </a>
                <span>:</span>
              </>
            )}
            &nbsp;
            {/* <!-- Lists the components that are causing the warning --> */}
            <span data-testid="warningComponents">{warningComponents.join(', ')}</span>
          </p>

          {/* <!-- Directs admins to the Administration view so they can see more details --> */}
          {isUserAdmin && (
            <a href="/administration">
              <span>{t('SYSTEM_WARNINGS.INFO_TEXT_ADMIN_MORE')}</span>
            </a>
          )}
        </div>
      </Alert>
    );
  }

  // Warning banner that displays a general message set by the system admin
  if (showSystemMessage) {
    return (
      <Alert
        id="systemMessageBanner"
        testId="systemMessageBanner"
        variant="warning"
        dismissible
        onClose={() => {
          setSystemMessage('');
          setSystemMessageHash(systemMessage);
        }}
        tooltip={t('CLOSE')}
        tooltipPlacement="top"
        extraClassNames="flexColumnContainer flexCenter warningBannerHeight">
        <ContainerWithHTML id="systemMessageText" content={systemMessage} extraClassNames="flexSelfCenter" />
      </Alert>
    );
  }

  // Warning banner that displays a message if auditing is enabled without an appropriate license
  if (showAuditWarning) {
    return (
      <Alert
        id="auditWarningBanner"
        testId="auditWarningBanner"
        variant="warning"
        extraClassNames="pt20 pb20 flexColumnContainer flexCenter warningBannerHeight">
        <img src={seeqLogoDarkBlueSm} className="mr25 ml15" />
        <span id="auditWarningText" className="flexSelfCenter">
          {t('SYSTEM_WARNINGS.AUDIT')}
        </span>
      </Alert>
    );
  }

  // Warning banner that displays a message that the system is approaching the licensed user limit
  if (showUserLimitWarning) {
    return (
      <Alert
        id="userLimitWarningBanner"
        testId="userLimitWarningBanner"
        variant="warning"
        dismissible
        onClose={disableUserLimitWarning}
        extraClassNames="pt20 pb20 flexColumnContainer flexCenter warningBannerHeight">
        <img src={seeqLogoDarkBlueSm} className="mr25 ml15" />
        <span id="userLimitWarningText" className="flexSelfCenter">
          {t('SYSTEM_WARNINGS.USER_LIMIT_WARNING', {
            count: userLimitRestrict,
            currentCount: userCount,
          })}
        </span>
      </Alert>
    );
  }

  // Warning banner that displays a message that the system is over the licensed user limit
  if (showUserLimitRestriction) {
    return (
      <Alert
        id="userLimitRestrictionBanner"
        testId="userLimitRestrictionBanner"
        variant="warning"
        extraClassNames="pt20 pb20 flexColumnContainer flexCenter warningBannerHeight">
        <img src={seeqLogoDarkBlueSm} className="mr25 ml15" />
        <span id="userLimitRestrictionText" data-testid="userLimitRestrictionText" className="flexSelfCenter">
          {t('SYSTEM_WARNINGS.USER_LIMIT_RESTRICTION', {
            count: userCount,
          })}
        </span>
      </Alert>
    );
  }

  return <></>;
};
